import { useExperimentStore } from "@func-core/core/data/repositories";
import { EXPERIMENTS } from "@func-core/core/domain/constants";
import { Country } from "@func-core/core/domain/view-models/country-view-model";
import {
  hashString,
  isExperimentEligible,
  selectVariant,
} from "@func-core/core/domain/view-models/experiment-view-model";
import { generateUuidV4 } from "@func-core/core/utils/uuid";
import { rollbar, RollbarLogArgument } from "@utils/rollbar";
import { Utms } from "@utils/utm";

export type InitializeExperimentsOptions = {
  country: Country;
  leadEmail?: string;
};

const getRandomExperimentUUID = (): string => generateUuidV4();

const getAnonymousIdentifier = (
  getAnonymousHash: () => string | undefined,
  storeAnonymousHash: (anonKey: string, countryCodeIso: string) => void,
  countryIso: string
): string => {
  const anonymousHash = getAnonymousHash();
  if (anonymousHash) return anonymousHash;

  const anonymousIdentifier = getRandomExperimentUUID();
  storeAnonymousHash(anonymousIdentifier, countryIso);

  return anonymousIdentifier;
};

const isAffiliateUtmValid = (utms: Utms): boolean => {
  const AFFILIATE_UTM = "affiliate";

  return utms.first_utm_medium === AFFILIATE_UTM || utms.last_utm_medium === AFFILIATE_UTM;
};

export const verifyExperimentOwnership = (leadKbKey: string, countryCodeIso: string): boolean => {
  try {
    const experimentStore = useExperimentStore.getState();
    const storedUserHash = experimentStore.getUserHash();

    if (!storedUserHash) return false;

    const expectedUserHash = hashString(`user-${leadKbKey}-${countryCodeIso}`).toString();

    return storedUserHash === expectedUserHash;
  } catch (error) {
    rollbar.error("Failed to verify experiment ownership", error as RollbarLogArgument);

    return false;
  }
};

export const initializeExperiments = ({
  country,
  leadEmail,
}: InitializeExperimentsOptions): void => {
  try {
    const { storeUserHash, storeAnonymousHash, getAnonymousHash, saveAssignment } =
      useExperimentStore.getState();

    const anonymousIdentifier = getAnonymousIdentifier(
      getAnonymousHash,
      storeAnonymousHash,
      country.iso
    );

    if (leadEmail) {
      storeUserHash(leadEmail, country.iso);
    }

    if (EXPERIMENTS.length === 0) return;

    EXPERIMENTS.forEach((experiment) => {
      if (!isExperimentEligible(experiment, country)) return;
      if (!experiment.isPreQuotes && !leadEmail) return;

      let hash = 0;
      if (experiment.isPreQuotes) {
        hash = hashString(`${anonymousIdentifier}-${experiment.id}`);
      } else {
        hash = hashString(`${leadEmail}-${experiment.id}`);
      }

      const selectedVariant = selectVariant(experiment, hash);

      saveAssignment(experiment.id, selectedVariant.id, experiment.isPreQuotes);
    });
  } catch (error) {
    rollbar.error(`Failed to initialize experiments`, error as RollbarLogArgument);
  }
};
